import { useEffect, useState } from 'react';
import { X, Share, Plus } from 'lucide-react';

const DISMISS_KEY = 'sereno_crm_install_dismissed_until';
const DISMISS_DAYS = 60;
const SHOW_DELAY_MS = 8000;

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = ua.includes('Macintosh') && 'ontouchend' in document;
  return iOS || iPadOS;
}

function isIosSafari(): boolean {
  if (!isIos()) return false;
  const ua = window.navigator.userAgent;
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

function isDismissed(): boolean {
  try {
    const until = localStorage.getItem(DISMISS_KEY);
    if (!until) return false;
    return Date.now() < Number(until);
  } catch {
    return false;
  }
}

function dismiss() {
  try {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
  } catch {
    // ignore storage errors
  }
}

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;

    let delayTimer: ReturnType<typeof setTimeout> | undefined;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      delayTimer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS has no beforeinstallprompt; show manual hint banner.
    if (isIosSafari()) {
      delayTimer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    }

    const onInstalled = () => {
      setVisible(false);
      dismiss();
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (delayTimer) clearTimeout(delayTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setVisible(false);
      dismiss();
      return;
    }
    // iOS: reveal manual instructions.
    setShowIosHint(true);
  };

  const handleClose = () => {
    setVisible(false);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pointer-events-none">
      <div className="pointer-events-auto mx-auto flex max-w-md flex-col gap-3 rounded-2xl border border-black/5 bg-white p-4 shadow-2xl dark:border-white/10 dark:bg-gray-800">
        <div className="flex items-start gap-3">
          <img
            src="/icon-192.png"
            alt="CRM Sereno"
            className="h-11 w-11 rounded-xl shadow-sm"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#0B1D2E] dark:text-gray-100">
              Instala CRM Sereno
            </p>
            <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">
              Añádelo a tu pantalla de inicio para acceder más rápido, a pantalla
              completa y sin barra del navegador.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Cerrar"
            className="-mr-1 -mt-1 rounded-lg p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X size={18} />
          </button>
        </div>

        {showIosHint ? (
          <p className="rounded-xl bg-[#FAF7F0] px-3 py-2 text-xs leading-relaxed text-[#0B1D2E] dark:bg-gray-700 dark:text-gray-100">
            En tu iPhone: pulsa{' '}
            <Share size={14} className="inline align-text-bottom" /> Compartir y
            luego{' '}
            <span className="font-medium">
              «Añadir a pantalla de inicio»{' '}
              <Plus size={14} className="inline align-text-bottom" />
            </span>
            .
          </p>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleInstall}
              className="flex-1 rounded-xl bg-[#0B1D2E] px-3 py-2 text-sm font-medium text-white hover:opacity-90"
            >
              Instalar
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-xl px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Ahora no
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
